import { AgGridReact } from "ag-grid-react";
import { useMemo } from "react";
import {
    AllCommunityModule,
    ColDef,
    colorSchemeDark,
    ModuleRegistry,
    themeQuartz,
} from "ag-grid-community";
import { COMING_SOON } from "@/entities/CreditBox/CreditBox.types";

ModuleRegistry.registerModules([AllCommunityModule]);

const myTheme = themeQuartz.withPart(colorSchemeDark).withParams({
    accentColor: "#6B7280",
    backgroundColor: "#1E293B",
    borderColor: "#334155",
    browserColorScheme: "dark",
    chromeBackgroundColor: {
        ref: "foregroundColor",
        mix: 0.07,
        onto: "backgroundColor",
    },
    fontFamily: {
        googleFont: "Inter",
    },
    foregroundColor: "#FFFFFF",
    headerFontSize: 14,
    headerVerticalPaddingScale: 1,
    cellHorizontalPaddingScale: 1.5,
    rowHoverColor: "#1E293B",
    rowBorder: {
        color: "#0f172a",
    },
    rangeSelectionBorderStyle: "none",
    pinnedColumnBorder: "none",
    headerHeight: 42,
});

export interface MemoTableProps<T> {
    data: T[];
    columns: ColDef[];
}

export function MemoTable<T>({ data, columns }: MemoTableProps<T>) {
    const columnDefs = useMemo<ColDef[]>(
        () =>
            columns.map((col) => ({
                ...col,
                headerClass: col.headerClass || "heading-s",
                flex: col.flex ?? 1,
                minWidth: col.minWidth ?? 140,
                wrapHeaderText: col.wrapHeaderText ?? true,
                autoHeaderHeight: col.autoHeaderHeight ?? true,
                cellStyle: col.cellStyle || {
                    display: "flex",
                    justifyContent: "start",
                    alignItems: "center",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    lineHeight: "1.4",
                    padding: "10px 16px",
                },
                cellClassRules: {
                    "text-disabled": (params) => {
                        return params.value === COMING_SOON;
                    },
                    ...col.cellClassRules,
                },
            })),
        [columns],
    );

    const defaultColDef = useMemo<ColDef>(
        () => ({
            resizable: true,
            sortable: false,
            filter: false,
            wrapText: true,
            autoHeight: true,
            wrapHeaderText: true,
            autoHeaderHeight: true,
            suppressHeaderMenuButton: true,
            suppressCellFlash: true,
            animateRows: false,
            cellStyle: {
                display: "flex",
                alignItems: "center",
                whiteSpace: "normal",
                wordWrap: "break-word",
                lineHeight: "1.4",
                padding: "12px 12px",
            },
        }),
        [],
    );

    return (
        <div
            className="ag-theme-quartz-dark overflow-hidden rounded"
            style={{ height: "auto" }}
        >
            <AgGridReact
                theme={myTheme}
                rowData={data}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                domLayout="autoHeight"
                suppressAnimationFrame={true}
                suppressRowTransform={true}
                animateRows={false}
            />
        </div>
    );
}
